<template>
    <Rows-container 
        ref="rowsContainer" 
        class="vod-lines"
        :items-props="partLines"
        @on-down="onDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
                @show-all="showAll"
                @plus="plus"
            />
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, nextTick, onMounted, watch } from "vue"
    import { useRoute, useRouter } from 'vue-router';
    import { useI18n } from "vue-i18n";
    import { isFizzTV, type Genre } from "@ca.vmedia.tv/domains";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { Strings } from "@/locales/types/enums";
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useLinesVodStore } from "@/stores/LinesVod";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import { useApplicationStore } from "@/stores/Application";
    import { useParentControlStore } from "@/stores/ParentControl";
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import type { ExposeInterface } from "./types";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";
    import { useVodPageStore } from "@/stores/VodPage";

    const VodPageStore = useVodPageStore();
    const {
        setCurrentGenre,
    } = VodPageStore;

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);


    const ApplicationStore = useApplicationStore();
    const {
        getFilterVod
    } = storeToRefs(ApplicationStore);
    const {
        setFilter
    } = ApplicationStore;

    const LinesVodStore = useLinesVodStore();
    const {
        getInternalGenres,
        getCardsTabsGenres,
        getCardsNewShows,
        getCardsNewMovies,
        getCardsRecommendedShows,
        getCardsRecommendedMovies,
        getCardsContinueWatching,
        getCardsMostPopular,
        getCardsNewReleases,
        getCardsWatchAgain,
        getCardsFilters,
        getCounterLinesVod,
        getChannelCards
    } = storeToRefs(LinesVodStore);

    const { getCardsGenre, increaseCounterLinesVod } = LinesVodStore;

    const { lineViewRows } = useAppConfig();
    const emit = defineEmits(['show-all', 'plus', 'list-item-selected', 'ready']);
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const selectedItem: Ref<IEventSelected | null> = ref(null);
    const getChannelsTitle: string = isFizzTV ? t(Strings.VOD_FIZZ_CHANNELS).toString() : t(Strings.VOD_CHANNELS).toString();
    const listCurrentGenres: Ref<Record<string, Genre>> = ref({});
    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesVod";
    const { shelfKeys, VodCounterLines }: typeof AppConstants = AppConstants;

    const watchAgain: ComputedRef<RowConfig[]> = computed(() => {
        const itemContent = {
            keyData: shelfKeys.vodWatchAgainCards,
            title: t(Strings.HOME_WATCH_AGAIN).toString(),
            ref: shelfKeys.vodWatchAgainCards,
            lineId: shelfKeys.vodWatchAgainCards,
            skeleton: SkeletonName.MOVIE_CARD_SKELETON,
            cardItems: getCardsWatchAgain.value
        };

        return [itemContent];
    });

    const tabsGenres: ComputedRef<RowConfig[]> = computed(() => {
        const itemContent: RowConfig = {
            keyData: shelfKeys.tabsGenresCards,
            title: "",
            ref: shelfKeys.tabsGenresCards,
            lineId: shelfKeys.tabsGenresCards,
            skeleton: SkeletonName.TABSGENRES_CARD_SKELETON,
            cardItems: getCardsTabsGenres.value,
        };
        return [itemContent];
    });

    const itemsGenres: ComputedRef<RowConfig[]> = computed(() => {
        if (getInternalGenres.value === null) {
            return [];
        }
        
        const items: RowConfig[] = [];
        
        getInternalGenres.value .forEach((genre, index) => {
            const itemContent: RowConfig = {
                keyData: shelfKeys.genresCards,
                title: genre.name,
                ref: shelfKeys.genresCards,
                lineId: `${shelfKeys.genresCards}_${index}`,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsGenre(genre.id),
                genre: genre
            };

            listCurrentGenres.value[`${shelfKeys.genresCards}_${index}`] = genre;
            items.push(itemContent);
        });
        
        return items;
    });
    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.channelCards,
                title: getChannelsTitle,
                ref: shelfKeys.channelCards,
                lineId: shelfKeys.channelCards,
                skeleton: SkeletonName.CHANNEL_CARD_SKELETON,
                cardItems: getChannelCards.value
            },
            {
                keyData: shelfKeys.newShowsCards,
                title: t(Strings.VOD_NEW_SHOWS).toString(),
                ref: shelfKeys.newShowsCards,
                lineId: shelfKeys.newShowsCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsNewShows.value
            },
            {
                keyData: shelfKeys.newMoviesCards,
                title: t(Strings.VOD_NEW_MOVIES).toString(),
                ref: shelfKeys.newMoviesCards,
                lineId: shelfKeys.newMoviesCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsNewMovies.value
            },
            {
                keyData: shelfKeys.recommendedShowsCards,
                title: t(Strings.VOD_RECOMMENDED_SHOWS).toString(),
                ref: shelfKeys.recommendedShowsCards,
                lineId: shelfKeys.recommendedShowsCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsRecommendedShows.value
            },
            {
                keyData: shelfKeys.recommendedMoviesCards,
                title: t(Strings.VOD_RECOMMENDED_MOVIES).toString(),
                ref: shelfKeys.recommendedMoviesCards,
                lineId: shelfKeys.recommendedMoviesCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsRecommendedMovies.value
            },
            {
                keyData: shelfKeys.vodContinueWatchingCards,
                title: t(Strings.HOME_FIZZ_CONTINUE_WATCHING).toString(),
                ref: shelfKeys.vodContinueWatchingCards,
                lineId: shelfKeys.vodContinueWatchingCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsContinueWatching.value
            },
            {
                keyData: shelfKeys.mostPopularCards,
                title: t(Strings.VOD_MOST_POPULAR).toString(),
                ref: shelfKeys.mostPopularCards,
                lineId: shelfKeys.mostPopularCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsMostPopular.value
            },
            {
                keyData: shelfKeys.newReleasesCards,
                title: t(Strings.VOD_NEW_RELEASES).toString(),
                ref: shelfKeys.newReleasesCards,
                lineId: shelfKeys.newReleasesCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsNewReleases.value
            },
            {
                keyData: shelfKeys.filtersCards,
                title: "",
                ref: shelfKeys.filtersCards,
                lineId: shelfKeys.filtersCards,
                skeleton: SkeletonName.TABSGENRES_CARD_SKELETON,
                cardItems: getCardsFilters.value,
            },
            ...tabsGenres.value,
            ...itemsGenres.value,
            ...watchAgain.value
        ].filter((item) => lineViewRows.vod.includes(item.keyData))
        .sort((a, b) => lineViewRows.vod.indexOf(a.keyData) - lineViewRows.vod.indexOf(b.keyData));
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesVod.value));
    const checkFilter = (): void => {
        if (getFilterVod.value && !route.query.filter) {
            router.replace({ path: linkItems.onDemand.routerPath, query: { "filter": `${getFilterVod.value}`}}).catch(()=>{});
        } else if (!getFilterVod.value && route.query.filter) {
            setFilter(route.query.filter as string);
        }
    };

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };
    
    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesVod.value >= countLoadedRows) {
            void nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= VodCounterLines) {
            const isIncreasing = (counter.value % VodCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesVod();
                counter.value++;
            }
        }


    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
        
        if (selectedItem.value !== null && selectedItem.value.lineId === shelfKeys.tabsGenresCards) {
            setCurrentGenre(listCurrentGenres.value[`${shelfKeys.genresCards}_${selectedItem.value.item.getId()}`]);
        }
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        }).catch((error)=> {
            Logger.captureError(error)
        });
    };

    const onDownRows = (): void => {
        if (getCounterLinesVod.value < allLines.value.length) {
            increaseCounterLinesVod();
        }
    };

    const showAll = (val: IEventSelected): void => {
        if (val.lineId.startsWith(shelfKeys.genresCards)) {
            setCurrentGenre(listCurrentGenres.value[val.lineId]);
        } 
        
        emit("show-all", val);
    };
    const plus = (lineId: string): void => {
        emit("plus", lineId);
    };
    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    onMounted(() => {
        void nextTick((): void => {
            checkFilter();
        });
    });

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>